/**
 * MembraneLanguage — client-safe translation layer.
 *
 * Two tiers:
 *   TRANSLATABLE — infra/architecture terms → client-facing copy
 *   INTELLIGENCE — internal vocabulary → redacted entirely (never shown to clients)
 *
 * The operator (principal role) bypasses this layer — they see raw frontier vocabulary.
 *
 * Ported from spec (v4 P1/A1) into the clean control-plane repo.
 * No fork sludge — this is a clean implementation of the membrane contract.
 */

// ── TRANSLATABLE tier ──────────────────────────────────────────────────────
// Infrastructure terms mapped to client-safe copy.
const TRANSLATABLE: Record<string, string> = {
  // Platform terms
  "GRAYMANTLE": "the GetRida platform",
  "control-server": "your workspace",
  "control plane": "your dashboard",
  "tenant-runtime": "your agent runtime",
  "platform Postgres": "your data store",
  "Redis": "your session store",
  "Mongo": "your document store",
  "Minio": "your file storage",
  "Tailchat": "your community",
  "Coolify": "deployment system",

  // Frontier terms (F-stages → client ontology)
  "F0": "intake",
  "F1": "discovery",
  "F2": "proposal",
  "F3": "routing",
  "F4": "governance",
  "F5": "your offer builder",
  "F5.5": "preparation",
  "F6": "your workspace",
  "F7": "agent setup",
  "F8": "agent supervision",
  "F9": "agent execution",
  "F10": "quality review",
  "F11": "proof",
  "F12": "your authority",
  "F13A": "go live",
  "F13B": "your business",
  "F13": "delivery",

  // Schema terms
  "Build Spec": "your agent blueprint",
  "Greenfield": "your workspace",
  "Droid Mission": "agent task",
  "Governor": "agent supervisor",
  "mission packet": "agent task list",
  "assembly": "agent setup",
  "arbitration": "quality check",
  "finality": "delivery confirmation",
  "writeback": "your business records",
  "standards": "quality standards",

  // Conveyance
  "Conveyance": "your agent handoff",
  "sovereign book": "your business records",
  "register": "your agent's work history",
  "coverage meter": "completion tracker",
  "causal spine": "work timeline",
  "lamport": "work order",
  "receipt": "work record",

  // Provisioning / Gate
  "Provisioning": "account setup",
  "Gate": "your agent gateway",
  "binding": "connection",
  "message-object": "your message",

  // Agent / Pi
  "Pi": "your agent",
  "capability registry": "agent skills",
  "designated runtime": "agent session",

  // Envoy
  "Envoy": "your outreach system",
  "campaign": "outreach campaign",
  "sequence": "follow-up sequence",
  "lead": "contact",
  "positive reply": "interested reply",
  "suppression": "do-not-contact",
  "Highlights": "campaign highlights",
  "intelligent outreach campaigns": "your outreach campaigns",

  // Inhabited Spec (Surface/F10)
  "Inhabited Spec": "your working Spec",
  "inhabited Spec": "your working Spec",
  "Your inhabited Spec": "your working Spec",
  "Your Spec": "your Spec",
  "Surface/F10": "your workspace",

  // Worker / Edge
  "Cloudflare Worker": "edge service",
  "D1": "edge database",
  "R2": "file storage",
  "wrangler": "deployment tool",
};

// ── INTELLIGENCE tier ──────────────────────────────────────────────────────
// Internal vocabulary that must NEVER appear in client-facing copy.
const INTELLIGENCE: ReadonlySet<string> = new Set([
  "covenant",
  "score-record",
  "F-chain",
  "semantic quarantine",
  "clean boundary",
  "constraint x",
  "stub-kill",
  "hel-spec",
  "redline",
  "ontology-native",
  "deterministic intake",
  "object construction",
  "thesis",
  "predicate",
  "evidence boundary",
  "authority boundary",
  "observable consequence",
  "source ordinal",
  "vector clock",
  "closure state",
  "lineage edge",
  "predecessor-only",
  "designated-runtime manifest",
  "content-addressed immutable",
  "cross-client memory",
  "cross-build memory",
  "execution realm",
  "causal source prefix",
  "fresh session",
  "forbidden actions",
  "model policy",
  "runtime policy",
]);

// ── Public API ─────────────────────────────────────────────────────────────

/**
 * Translate a string from internal vocabulary to client-safe copy.
 * If the term is not in the TRANSLATABLE map, return it unchanged.
 */
export function translate(input: string): string {
  if (!input) return input;
  let result = input;
  // Sort keys by length descending so longer matches win
  const sortedKeys = Object.keys(TRANSLATABLE).sort((a, b) => b.length - a.length);
  for (const key of sortedKeys) {
    // Case-insensitive whole-word or phrase match
    const re = new RegExp(escapeRegExp(key), "gi");
    result = result.replace(re, TRANSLATABLE[key]);
  }
  return result;
}

/**
 * Scrub a string of all INTELLIGENCE-tier terms.
 * Replaces any occurrence with [redacted].
 */
export function scrub(input: string): string {
  if (!input) return input;
  let result = input;
  for (const term of INTELLIGENCE) {
    const re = new RegExp(escapeRegExp(term), "gi");
    result = result.replace(re, "[redacted]");
  }
  return result;
}

/**
 * Assert that a string is client-safe:
 *   - TRANSLATABLE terms have been translated
 *   - INTELLIGENCE terms have been scrubbed
 * Returns true if safe, false if any raw internal vocabulary remains.
 */
export function assertClientSafe(input: string): boolean {
  if (!input) return true;
  // Check for any remaining INTELLIGENCE terms
  for (const term of INTELLIGENCE) {
    const re = new RegExp(escapeRegExp(term), "gi");
    if (re.test(input)) return false;
  }
  return true;
}

/**
 * Full membrane pass: translate then scrub.
 * Use this for any string that will be rendered to a client.
 */
export function membranePass(input: string): string {
  return scrub(translate(input));
}

/**
 * Process an object recursively, applying membranePass to all string values.
 */
export function membraneObject<T>(obj: T, bypass: boolean = false): T {
  if (bypass) return obj;
  if (typeof obj === "string") return membranePass(obj) as unknown as T;
  if (Array.isArray(obj)) return obj.map((item) => membraneObject(item, bypass)) as unknown as T;
  if (obj && typeof obj === "object") {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      result[key] = membraneObject(value, bypass);
    }
    return result as unknown as T;
  }
  return obj;
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// ── Re-exports for testing ─────────────────────────────────────────────────
export { TRANSLATABLE, INTELLIGENCE };
